/**
 * React hook for monitoring memory usage and triggering cleanup
 *
 * Monitors memory usage at regular intervals and provides callbacks
 * for different memory pressure levels.
 */

import { useEffect, useRef, useCallback } from "react";
import {
  getMemoryStats,
  getMemoryPressure,
  MemoryPressure,
  type MemoryStats,
} from "./memoryMonitor";

export interface UseMemoryMonitorOptions {
  /**
   * Interval in milliseconds to check memory usage
   * @default 30000 (30 seconds)
   */
  interval?: number;

  /**
   * Callback fired when memory pressure changes
   */
  onPressureChange?: (pressure: MemoryPressure, stats: MemoryStats | null) => void;

  /**
   * Callback fired when memory pressure is high or critical
   * Use this to trigger cleanup operations
   */
  onHighMemory?: (stats: MemoryStats | null) => void;
}

/**
 * Hook to monitor memory usage and trigger cleanup when needed
 *
 * @example
 * ```typescript
 * useMemoryMonitor({
 *   interval: 30000, // Check every 30 seconds
 *   onHighMemory: (stats) => {
 *     // Trigger cleanup when memory is high
 *     queryClient.clear(); // Clear React Query cache
 *     // Or more targeted cleanup
 *   }
 * });
 * ```
 */
export function useMemoryMonitor(options: UseMemoryMonitorOptions = {}) {
  const { interval = 30000, onPressureChange, onHighMemory } = options;

  const lastPressureRef = useRef<MemoryPressure>(MemoryPressure.LOW);
  const highMemoryCallbackFiredRef = useRef(false);

  const checkMemory = useCallback(() => {
    const stats = getMemoryStats();
    const pressure = getMemoryPressure(stats);

    // Fire pressure change callback if pressure changed
    if (pressure !== lastPressureRef.current) {
      onPressureChange?.(pressure, stats);
      lastPressureRef.current = pressure;
      highMemoryCallbackFiredRef.current = false; // Reset for new pressure level
    }

    // Fire high memory callback once per pressure level
    if (
      (pressure === MemoryPressure.HIGH || pressure === MemoryPressure.CRITICAL) &&
      !highMemoryCallbackFiredRef.current
    ) {
      onHighMemory?.(stats);
      highMemoryCallbackFiredRef.current = true;
    }
  }, [onPressureChange, onHighMemory]);

  useEffect(() => {
    // Initial check
    checkMemory();

    // Set up interval
    const intervalId = setInterval(checkMemory, interval);

    return () => {
      clearInterval(intervalId);
    };
  }, [checkMemory, interval]);
}
